<template>
    <div class="change-profile">
        <div class="middle">
            <div class="roles form-field">
                <div class="title">Роли</div>
                <div class="value roles-container badges">
                    <div v-for="id in rolesIds" :key="id"
                         class="role-item item gray">{{ roleMap[id].name }}
                    </div>
                </div>
            </div>
            <div class="form-field">
                <div class="title">Фамилия Имя Отчество</div>
                <div class="value">
                    <input v-model.trim="v.user.fio.$model"
                           :class="{ error: v.user.fio.$error }"
                           :disabled="isLdapEmail"
                           placeholder="Введите Фамилию, Имя, Отчество"
                           class="fio"
                           type="text"/>

                    <ErrorMessageTooltip :value="v.user.fio" bind-to=".fio" />
                </div>
            </div>
            <div class="form-field">
                <div class="title">Должность</div>
                <div class="value">
                    <input v-model="v.user.post.$model"
                           :disabled="isLdapEmail"
                           :class="{ error: v.user.post.$error }"
                           placeholder="Введите должность"
                           class="post"
                           type="text"/>

                    <ErrorMessageTooltip :value="v.user.post" bind-to=".post" />
                </div>
            </div>
            <div class="form-field">
                <div class="title">Подразделение</div>
                <div class="value">
                    <textarea v-model="v.user.department.$model"
                              :class="{ error: v.user.department.$error }"
                              :disabled="isLdapEmail"
                              class="department"
                              maxlength="1000"
                              placeholder="Введите подразделение" />

                    <ErrorMessageTooltip :value="v.user.department" bind-to=".department" />
                </div>
            </div>
            <div class="form-field">
                <div class="title">Телефон</div>
                <div class="value">
                    <MaskedInput v-model="v.user.phone.$model"
                                 :a-class="{ error: v.user.phone.$error }"
                                 class="masked-phone"
                                 mask="+7 (###) ###-##-##"
                                 placeholder="+7 ___ ___-__-__"
                    />

                    <ErrorMessageTooltip :value="v.user.phone" bind-to=".masked-phone input" />
                </div>
            </div>
            <div class="form-field">
                <div class="title">Рассылка обновлений</div>
                <div class="value">
                    <Combobox ref="period"
                              v-model="subModel.period"
                              :class="{ error: v.subModel.period.$error }"
                              :options="dicts.subscriptionOptions"
                              :toTitle="it => it.name"
                              class="item subscription"
                              placeholder="Выберите частоту рассылки"/>
                </div>
            </div>
            <div class="form-field">
                <div class="title">Дополнительная информация</div>
                <textarea v-model.trim="v.user.extra.$model"
                          :class="{ error: v.user.extra.$error }"
                          class="value extra"
                          placeholder="Укажите любую дополнительную информацию о себе"
                />

                <ErrorMessageTooltip :value="v.user.extra" bind-to=".extra" />
            </div>

            <div class="form-field">
                <div class="value">
                    <button :disabled="isLdapEmail" class="iconed primary no-border links"
                            @click="changePassword()">
                        <IconKey class="icon"/>
                        <span>Сменить пароль</span>
                    </button>
                </div>
            </div>
        </div>

        <div class="footer two-sided">
            <div class="left">
                <a class="logout iconed"
                   href="javascript:"
                   @click="$emit('logout')">
                    <IconLogout class="icon" />
                    <span class="text">Выйти из системы</span>
                </a>
            </div>

            <div class="right">
                <input class="ghost action"
                       type="button"
                       value="Отмена"
                       @click="close()" />
                <input :disabled="v.$invalid || !changed"
                       :value="saveText"
                       type="button"
                       class="primary action save"
                       @click="save()" />
            </div>
        </div>

        <ChangePassword ref="changePassword" />
    </div>
</template>

<script>
import { isLdapEmail } from '@/common/allowed.emails';
import { DICT_SUBSCRIPTIONS, SUBSCRIPTION_CONFIG_KEY } from '@/common/page_comp/News/SubTab/common/config.js';
import { extraTextRulesDefault } from '@/common/util/validations/extra.text.js';
import { getSubscriptionConfig, saveSubscriptionConfig } from '@/restapi/news/news.subscription.js';
import { rolesAll } from '@/restapi/role.js';
import { currentUser, updateSession } from '@/common/session.js';
import { getEmailDomain, isMobile } from '@/common/util/util.js';
import { allowedEmailRule, emailRulesDefault } from '@/common/util/validations/email.js';
import { fioRulesDefault } from '@/common/util/validations/fio.js';
import { maxLengthRule, requiredRule } from '@/common/util/validations/main.js';
import { phoneRulesDefault } from '@/common/util/validations/phone.js';
import { postRulesDefault } from '@/common/util/validations/post.js';
import { profileUpdate } from '@/restapi/user/user.js';
import { showError, showNotice } from '@dsee/components/popups/toast.js';
import { deepCopy, deepEquals, deepFreeze } from '@dsee/util/objects.js';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Local from '../../util/local.js';
import { toMap } from '@dsee/util/arrays.js';

import IconKey from './icons/key.svg?component';
import ChangePassword from '@/common/page_comp/Profile/ChangePassword.vue';

export default {
    components: {
        ChangePassword,
        IconKey,
    },
    setup() {
        return {
            v: useVuelidate({ $autoDirty: true }),
        };
    },
    data() {
        return {
            user: {
                email: '',
                fio: '',
                post: '',
                phone: '',
                extra: '',
                department: '',
            },
            rolesIds: [],
            allRoles: [],

            subModel: {
                config: null,
                period: 'NEVER',
            },

            subModelOriginal: {},

            dicts: {
                allRoles: [],
                subscriptionOptions: [...DICT_SUBSCRIPTIONS, { id: 'NEVER', name: 'Не отправлять' }],
            },
        };
    },
    computed: {
        saveText() {
            return isMobile() ? 'Сохранить' : 'Сохранить изменения';
        },
        roleMap() {
            return toMap(this.dicts.allRoles);
        },
        changed() {
            if (!deepEquals(this.subModel, this.subModelOriginal))
                return true;

            return !deepEquals(this.user, currentUser());
        },
        isLdapEmail() {
            return isLdapEmail(this.user.email);
        },
    },
    validations() {
        return {
            user: {
                fio: {
                    ...fioRulesDefault(),
                },
                phone: {
                    ...phoneRulesDefault(),
                },
                email: {
                    ...requiredRule(),
                    ...emailRulesDefault(),
                    ...allowedEmailRule(),
                },
                post: {
                    ...postRulesDefault(),
                },
                department: {
                    ...maxLengthRule(1000),
                },
                extra: {
                    ...extraTextRulesDefault(),
                },
            },
            subModel: {
                period: {
                    ...requiredRule(),
                },
            },
        };
    },
    async created() {
        await this.getAllRoles();
        await this.updateSubConfig();
        this.reset();
    },
    methods: {
        async getAllRoles() {
            this.dicts.allRoles = await rolesAll();
        },
        close() {
            this.$emit('close');
        },
        reset() {
            this.user = deepCopy(currentUser());
            this.rolesIds = this.user.roles || [];
            this.updateSubConfig();
            this.v.$reset();
        },
        changePassword() {
            this.$refs.changePassword.open();
        },
        async save() {
            this.v.$touch();

            if (this.v.$invalid)
                return;

            try {
                const data = {
                    fio: this.user.fio,
                    post: this.user.post,
                    phone: this.user.phone,
                    extra: this.user.extra,
                    department: this.user.department,
                };

                const user = await profileUpdate(data);
                updateSession(user);

                await this.saveSubConfig();

                this.close();
                showNotice('Данные пользователя изменены');
            } catch (e) {
                showError('Произошла ошибка при изменении данных пользователя');
                throw e;
            }
        },
        async updateSubConfig() {
            const config = await getSubscriptionConfig();
            const period = config.active ? config.period : 'NEVER';
            const subModel = {
                config,
                period,
            };

            this.subModelOriginal = deepFreeze(subModel);
            this.subModel = deepCopy(this.subModelOriginal);

            Local.put(SUBSCRIPTION_CONFIG_KEY, config);
        },
        async saveSubConfig() {
            const data = {
                active: this.subModel.period !== 'NEVER',
                period: this.subModel.period !== 'NEVER' ? this.subModel.period : this.subModel.config.period,
            };

            await saveSubscriptionConfig(data);
            Local.put(SUBSCRIPTION_CONFIG_KEY, data);
        },
    },
    watch: {
        changed(value) {
            this.$emit('changed', value);
        },
    },
};
</script>

<style lang="scss" scoped>
.change-profile {
    border-radius: 0;

    .email-error {
        color: #FF0C0C;
    }

    .roles {
        .roles-container {
            display: flex;
            flex-wrap: wrap;
            row-gap: 4px;
        }
    }

    .department {
        min-height: 100px;
        max-height: 150px;
    }

    .footer {
        display: flex;
        justify-content: space-between;
        flex-wrap: nowrap;

        .logout {
            font-size: 12px;
            line-height: 16px;
            opacity: .5;
        }

        .left {
        }

        .action {
            padding: 8px 20px;
        }
    }
}

.tooltip {
    .desc-fio {
        margin: 0;
        padding: 5px 20px;

        li {
            font-weight: 400;
            font-size: 12px;
        }
    }
}
</style>
